/**
 * Derivation of the legacy 4-character IAB US Privacy string from the GPP
 * USNAT (section 7) payload, per `services/specs/ads/us-privacy-signal-migration.md`
 * §5. Google Privacy & messaging serves no `__uspapi`, so under the GOOGLE_CMP
 * flag this is the only source of the legacy string (decision D1a).
 *
 * Contract highlights from the spec:
 * - Returns null when USNAT is not among the applicable sections. Never
 *   fabricate `1---`: that asserts "notice not applicable, not opted out",
 *   a claim we have not earned. Per-state sections (USCA etc.) are not USNAT
 *   and must not be mapped.
 * - Position 2 combines all three notice fields; a single 2 among them means
 *   the full notice was not given, so the honest answer is N.
 * - Position 3 is privacy-protective: any opt-out (1) wins, then any 2 is N,
 *   and only both-zero is `-`.
 * - Position 4 is `-` unless legal confirms LSPA coverage.
 * - Pure and synchronous; the identical mapping ships on iOS, Android and
 *   flint, pinned by the spec's §7 conformance vectors in each test suite.
 */

export const GPP_SECTION_USNAT = 7;

export type UsnatFields = {
  SharingNotice?: number;
  SaleOptOutNotice?: number;
  TargetedAdvertisingOptOutNotice?: number;
  SaleOptOut?: number;
  SharingOptOut?: number;
};

const noticeChar = (fields: UsnatFields): string => {
  const notices = [
    fields.SharingNotice ?? 0,
    fields.SaleOptOutNotice ?? 0,
    fields.TargetedAdvertisingOptOutNotice ?? 0,
  ];
  if (notices.every((v) => v === 0)) {
    return '-';
  }
  if (notices.some((v) => v === 2)) {
    return 'N';
  }
  return 'Y';
};

const optOutChar = (fields: UsnatFields): string => {
  const optOuts = [fields.SaleOptOut ?? 0, fields.SharingOptOut ?? 0];
  if (optOuts.some((v) => v === 1)) {
    return 'Y';
  }
  if (optOuts.some((v) => v === 2)) {
    return 'N';
  }
  return '-';
};

export const deriveUsPrivacyString = (
  applicableSections: number[] | undefined,
  usnatFields: UsnatFields | null | undefined,
): string | null => {
  if (!applicableSections?.includes(GPP_SECTION_USNAT) || !usnatFields) {
    return null;
  }
  return `1${noticeChar(usnatFields)}${optOutChar(usnatFields)}-`;
};
